import { useMemo, useState } from "react";
import { Table } from "antd";
import type { TableProps } from "antd";
import { useBusinessSettingLog } from "hooks/queries/business-settings";
import { BusinessSettingLogEntry } from "shared/types";
import { translate } from "shared/languages";
import { PAGE_SIZE } from "shared/constants";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import { th } from "shared/components/TableText";

// "Riwayat Perubahan", the second tab of a settings page: who changed which
// setting, when, from what to what, in the same form as a client package's
// Riwayat tab (PackageHistoryTable): one row per changed field, Waktu /
// Diubah Oleh / Jenis Perubahan / Sebelum / Sesudah, ten rows a page,
// newest first, the server paging and counting. Each value is printed
// through the page's own formatter so history and the confirm dialog use
// identical words. Hidden entirely when the request fails (an older server
// answers 404), so the page is unchanged until the server carries the log.
export const SettingsChangeLog = ({
  fields,
  labelOf,
  format,
}: {
  /** The settings keys this page owns. */
  fields: string[];
  labelOf: (field: string) => string;
  format: (field: string, value: BusinessSettingLogEntry["old_value"]) => string;
}) => {
  const [page, setPage] = useState(1);
  const log = useBusinessSettingLog(fields, page, PAGE_SIZE);

  const columns = useMemo<TableProps<BusinessSettingLogEntry>["columns"]>(
    () => [
      {
        title: th(translate("label.time")),
        dataIndex: "created_at",
        key: "created_at",
        width: "20%",
        render: (at: string) => <span>{formatDate(at, DEFAULT_DATE_FORMAT + ", HH:mm")}</span>,
      },
      {
        title: th(translate("label.edited_by")),
        dataIndex: "actor_name",
        key: "actor_name",
        width: "20%",
        render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      },
      {
        title: th(translate("label.field_changed")),
        dataIndex: "field",
        key: "field",
        width: "20%",
        render: (field: string) => labelOf(field),
      },
      {
        title: th(translate("label.before")),
        dataIndex: "old_value",
        key: "old_value",
        width: "20%",
        render: (value: BusinessSettingLogEntry["old_value"], row) => format(row.field, value),
      },
      {
        title: th(translate("label.after")),
        dataIndex: "new_value",
        key: "new_value",
        width: "20%",
        render: (value: BusinessSettingLogEntry["new_value"], row) => format(row.field, value),
      },
    ],
    [labelOf, format],
  );

  if (log.isError) return null;

  return (
    <Table<BusinessSettingLogEntry>
      showSorterTooltip={false}
      rowKey="id"
      columns={columns}
      dataSource={log.data?.entries ?? []}
      loading={log.isLoading}
      locale={{ emptyText: translate("class_settings.history.empty") }}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: log.data?.total ?? 0,
        showSizeChanger: false,
        onChange: setPage,
      }}
    />
  );
};

export default SettingsChangeLog;
